import isEqual from 'lodash/isEqual'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { createGlobalStyle } from 'styled-components'

import { TemporalCondition } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  DatePickerField,
  DatePickerFieldProps,
  FilterAccordion,
  HorizontalAlignment,
  useOnClickOutside,
} from '@avantstay/backoffice-core'

import { DatePickerCustomFilter } from '@new/__global/components/DatePickerCustomFilter'
import { getLabelWithPeriodReference } from '@new/domains/bookings/screens/bookingsList/components/Filters/BookingsListFilter.utils'

import { FiltersSharedProps, PeriodFilterRawData } from '../Filters.types'

interface DateRangePickerBasedFilterProps
  extends FiltersSharedProps,
    Omit<DatePickerFieldProps, 'dateValue' | 'onChange'> {
  initialValue?: PeriodFilterRawData
  onChange: (value?: PeriodFilterRawData) => void
  shouldDisplayDatePickerInput?: boolean
  shouldUpdateStateOnInitialValueChange?: boolean
  title?: string
  horizontalAlignment?: HorizontalAlignment
}

const GlobalStyle = createGlobalStyle`
.rdr-clear-button {
  position: absolute;
  top: 65px;
}
`

export const DateRangePickerBasedFilter: FC<DateRangePickerBasedFilterProps> = ({
  initialValue,
  isAccordionOpen,
  isSidebarFilter = false,
  onChange,
  shouldDisplayDatePickerInput,
  shouldUpdateStateOnInitialValueChange = false,
  title,
  horizontalAlignment,
  ...datePickerProps
}) => {
  const [localValue, setLocalValue] = useState<PeriodFilterRawData | undefined>(initialValue)
  const isSingleDayRange = !!localValue?.periodReference

  const wrapperRef = useRef<HTMLDivElement>(null)

  const getDateValue = () => {
    if (isSingleDayRange) {
      return localValue?.period?.split('_')[0]
    }

    return localValue?.period
  }

  const handleSubmitValue = () => {
    if (!isEqual(localValue, initialValue)) {
      onChange({ ...localValue, period: getDateValue() })
    }
  }

  useEffect(() => {
    if (isSidebarFilter) {
      handleSubmitValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue, isSidebarFilter])

  useEffect(() => {
    if (shouldUpdateStateOnInitialValueChange) {
      setLocalValue(initialValue)
    }
  }, [initialValue, shouldUpdateStateOnInitialValueChange])

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  useOnClickOutside(wrapperRef, !isSidebarFilter ? handleSubmitValue : () => {})

  const handleOnChangePeriod = (value?: string) => {
    setLocalValue(prev => ({
      ...prev,
      period: value,
    }))
  }

  const handleOnClear = () => {
    onChange(undefined)
    setLocalValue(undefined)
  }

  const handleOnChangePeriodReference = useCallback((value?: TemporalCondition) => {
    setLocalValue(prev => ({ ...prev, periodReference: value }))
  }, [])

  const filterValue = getLabelWithPeriodReference({ ...localValue, period: getDateValue() })

  return (
    <FilterAccordion
      isAccordionOpen={isAccordionOpen}
      title={title}
      onClearFilter={handleOnClear}
      filterValue={localValue && filterValue}
      isSidebarFilter={isSidebarFilter}
      isDateRange={!!localValue?.periodReference}
      shouldShowClearButton={isSidebarFilter}
      ref={wrapperRef}
    >
      <>
        <GlobalStyle />
        <DatePickerField.Small
          {...datePickerProps}
          dateValue={getDateValue() || ''}
          onChange={handleOnChangePeriod}
          shouldDisplayInput={shouldDisplayDatePickerInput}
          horizontalAlignment={
            (horizontalAlignment ?? isSidebarFilter)
              ? HorizontalAlignment.Left
              : HorizontalAlignment.Right
          }
          singleDateRange={isSingleDayRange}
          shouldAcceptEmptyDate
          shouldStartEmptyDateSelection={!localValue?.period}
          customDatePickerHeaderComponent={
            <DatePickerCustomFilter
              periodReferenceOption={localValue?.periodReference}
              onChangeOption={handleOnChangePeriodReference}
            />
          }
        />
      </>
    </FilterAccordion>
  )
}
